import React, { useState } from "react";
import { Play, ArrowRight, Phone, Volume2, Activity } from "lucide-react";

/**
 * Demo video section.
 * - Shows a styled poster card with a play button.
 * - On click, lazy-mounts the iframe (no third-party request on initial load).
 * - To wire up a real Loom: replace VIDEO_EMBED_URL below.
 *   e.g. "https://www.loom.com/embed/<videoId>?autoplay=1"
 */
const VIDEO_EMBED_URL =
  "https://www.loom.com/embed/PLACEHOLDER_VIDEO_ID?autoplay=1&hide_owner=true&hide_share=true&hideEmbedTopBar=true";

export default function DemoVideo() {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="demo-video"
      className="relative section-padding"
      data-testid="demo-video-section"
    >
      <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
      <div className="absolute left-1/2 top-10 w-[700px] h-[400px] -translate-x-1/2 bg-[#007AFF]/10 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute left-1/2 bottom-10 w-[500px] h-[300px] -translate-x-1/2 bg-[#39FF14]/8 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto reveal">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
            <span className="w-6 h-px bg-[#39FF14]" />
            Live Demo
            <span className="w-6 h-px bg-[#39FF14]" />
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
            See The AI Receptionist{" "}
            <span className="gradient-text-green">In Action.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed">
            Watch how the system answers calls, qualifies leads, books
            appointments, and routes conversations — automatically.
          </p>
        </div>

        {/* Video card */}
        <div
          className="mt-12 lg:mt-14 reveal"
          data-testid="demo-video-card-wrap"
        >
          <div
            className="relative mx-auto max-w-5xl rounded-2xl sm:rounded-3xl overflow-hidden glass-strong"
            style={{
              boxShadow:
                "0 40px 100px rgba(0,0,0,0.55), 0 0 80px rgba(57,255,20,0.08), inset 0 0 0 1px rgba(255,255,255,0.04)",
            }}
            data-testid="demo-video-card"
          >
            {/* Aspect-ratio wrapper (16/9) */}
            <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
              {playing ? (
                <iframe
                  src={VIDEO_EMBED_URL}
                  title="Core Scale AI Receptionist Demo"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid="demo-video-iframe"
                />
              ) : (
                <button
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 w-full h-full group cursor-pointer"
                  aria-label="Play AI Receptionist demo video"
                  data-testid="demo-video-play"
                >
                  {/* Poster (synthetic dashboard scene) */}
                  <PosterScene />

                  {/* Play button */}
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="relative flex items-center justify-center">
                      <span className="absolute inset-0 rounded-full bg-[#39FF14]/30 blur-2xl scale-150" />
                      <span className="absolute -inset-3 rounded-full border border-[#39FF14]/30 animate-pulse-glow" />
                      <span className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#39FF14] text-black flex items-center justify-center shadow-[0_0_44px_rgba(57,255,20,0.5)] group-hover:scale-105 transition-transform">
                        <Play
                          className="w-6 h-6 sm:w-7 sm:h-7 ml-1"
                          fill="currentColor"
                          aria-hidden="true"
                        />
                      </span>
                    </span>
                  </span>

                  {/* Bottom meta strip */}
                  <span className="absolute left-4 right-4 bottom-4 sm:left-6 sm:right-6 sm:bottom-6 flex items-center justify-between text-[11px] sm:text-xs font-mono uppercase tracking-widest text-zinc-300 pointer-events-none">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
                      <span className="live-dot" />
                      <span>2 min · live walkthrough</span>
                    </span>
                    <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10">
                      <Volume2 className="w-3 h-3 text-[#39FF14]" />
                      audio on
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* CTA beneath video */}
        <div className="mt-9 flex flex-col items-center gap-4 reveal">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href="https://cal.com/erik-carlson/secret"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#39FF14] text-black font-semibold hover:bg-white hover:shadow-[0_0_36px_rgba(57,255,20,0.5)] transition-all"
              data-testid="demo-video-cta-primary"
            >
              Get One Built For Your Business
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </a>
            <a
              href="tel:2149838371"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 hover:border-white/30 transition-all"
              data-testid="demo-video-cta-secondary"
            >
              <Phone className="w-4 h-4 text-[#39FF14]" />
              Or call the live demo
            </a>
          </div>
          <p className="text-xs text-zinc-500 font-mono">
            Live demo line · 214-983-8371
          </p>
        </div>
      </div>
    </section>
  );
}

const PosterScene = () => (
  <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#070707] to-[#0a0a0a] overflow-hidden">
    {/* Subtle grid */}
    <div className="absolute inset-0 grid-bg opacity-60" />

    {/* Faux dashboard floating panel — center-left */}
    <div className="absolute left-6 sm:left-12 top-1/2 -translate-y-1/2 w-[44%] hidden md:block">
      <div className="glass-strong rounded-2xl p-4">
        <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-3">
          <span>Incoming Call · Live</span>
          <span className="text-[#39FF14] inline-flex items-center gap-1.5">
            <span className="live-dot" />
            AI
          </span>
        </div>
        <div className="text-white font-semibold">Jamie Rivera</div>
        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
          +1 214 · qualified · routing
        </div>
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {[0.45, 0.7, 0.9].map((w, i) => (
            <div
              key={i}
              className="h-1 rounded-full bg-white/5 overflow-hidden"
            >
              <div
                className="h-full bg-gradient-to-r from-[#39FF14] to-[#007AFF]"
                style={{ width: `${w * 100}%` }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Faux booking confirmation — right */}
    <div className="absolute right-6 sm:right-12 top-1/3 -translate-y-1/2 hidden md:block">
      <div className="glass-strong rounded-2xl p-3 w-52">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#39FF14] flex items-center gap-1.5">
          <Activity className="w-3 h-3" />
          Booking Confirmed
        </div>
        <div className="mt-1.5 text-sm text-white">Tue · Mar 12 · 3:30 PM</div>
        <div className="text-[10px] text-zinc-500 font-mono mt-1">
          Synced → CRM · GCal
        </div>
      </div>
    </div>

    {/* Mobile compact label */}
    <div className="md:hidden absolute top-5 left-5 right-5 flex items-center justify-between">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        AI Receptionist · Demo
      </span>
      <span className="text-[10px] font-mono text-[#39FF14] inline-flex items-center gap-1.5">
        <span className="live-dot" />
        Live
      </span>
    </div>

    {/* Dark vignette */}
    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
  </div>
);
